import { Box } from '@mui/material';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeletePlaylist } from '~/api/playlists';
import { Tooltip } from '~/components/Tooltip';
import { DeleteButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import type { PlaylistDetail__Playlist as Playlist } from '~/pages/playlists/queries/queries.generated';

export interface ActionsProps {
  playlist: Playlist;
}

export const Actions = ({ playlist }: ActionsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const link = useLink();

  const [confirm, confirmProps] = useConfirmDialog();

  const [deletePlaylist] = useDeletePlaylist();

  const handleDelete = useCallback(async () => {
    if (!(await confirm())) return;
    await deletePlaylist({ variables: { playlistIds: [playlist.id] } });
    history.length > 1 ? navigate(-1) : navigate(link('/playlists'));
  }, [confirm, deletePlaylist, link, navigate, playlist.id]);

  if (!playlist.canUpdate.value) return <Box />;

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Tooltip title={playlist.canDestroy.reasons?.fullMessages.join(', ')}>
          <span>
            <DeleteButton disabled={!playlist.canDestroy.value} onClick={handleDelete} />
          </span>
        </Tooltip>
      </Box>
      <ConfirmDialog
        {...confirmProps}
        confirm={t('pages.playlists.detail.actions.delete_dialog.confirm')}
        cancel={t('common.dialogs.cancel')}
        deleteConfirm
        prompt={
          <Trans i18nKey="pages.playlists.detail.actions.delete_dialog.prompt">
            Deleting this Playlist will remove it from Shows where it is being used.
          </Trans>
        }
        title={t('pages.playlists.detail.actions.delete_dialog.title')}
      />
    </>
  );
};
